import React, { useState, useEffect, useRef } from "react";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import "./PhoneInput.scss";
import { parsePhoneNumberFromString } from "libphonenumber-js";

// ─── FULLY VERIFIED AGAINST react-phone-input-2 v2.15.1 SOURCE ───────────────
//
// constructor: guessSelectedCountry fires based on initial value.
//   If value="", it uses the `country` prop → correct flag on mount. ✅
//
// componentDidUpdate / updateFormattedNumber:
//   if (currentCountry && value.startsWith("+" + currentCountry.dialCode))
//     → keeps current country (no re-guess) ✅
//   else if (disableCountryGuess)
//     → keeps current country (n) instead of guessing ✅
//   → So once flag is set to PK on mount, updating value with disableCountryGuess
//     keeps PK flag regardless of what digits the value starts with. ✅
//
// STRATEGY:
//   1. Mount with value="" → constructor uses country prop → PK flag ✅
//   2. One render later (useEffect), set internalValue = nationalNumber
//      → componentDidUpdate fires → disableCountryGuess keeps PK flag ✅
//      → national number displayed without country code ✅
// ─────────────────────────────────────────────────────────────────────────────

const PhoneFormInput = ({
  label,
  divClassName,
  name,
  value,
  country,
  countryFieldName,
  handleBlur,
  error,
  touched,
  setFieldValue,
  placeholder,
  inputClass,
  errorClass,
  labelClass,
  containerClass,
  buttonClass,
  inputStyle,
  buttonStyle,
  ...rest
}) => {
  const resolvedCountry = String(country || "AU").toLowerCase();

  const [internalValue, setInternalValue] = useState("");
  const lastSentValueRef = useRef("");
  const phoneInputRef = useRef(null);

  useEffect(() => {
    if (value === lastSentValueRef.current) {
      return;
    }
    let valToSet = value || "";
    if (valToSet.startsWith("+")) {
      const digits = valToSet.replace(/\D/g, "");
      if (resolvedCountry === "au" && digits.startsWith("610")) {
        valToSet = "+61" + digits.slice(3);
      }
    }
    setInternalValue(valToSet);
  }, [value, resolvedCountry]);

  const handleInputChange = (phoneNumber, countryData) => {
    const format = countryData?.format || "";
    const dotCount = (format.match(/\./g) || []).length;
    const limit = dotCount > 0 ? dotCount : 15;

    let cleanVal = String(phoneNumber || "").replace(/\D/g, "");

    // Auto-strip accidental leading zero after country dial code (e.g. 610411... -> 61411...)
    const dialCode = String(countryData?.dialCode || "");
    if (dialCode && cleanVal.startsWith(dialCode + "0")) {
      cleanVal = dialCode + cleanVal.slice(dialCode.length + 1);
    }

    const formattedPhone = cleanVal ? ("+" + cleanVal) : "";
    const formattedLimit = cleanVal ? (limit + 1) : 15;

    let finalPhone = formattedPhone;
    if (formattedPhone.length > formattedLimit) {
      const truncatedClean = cleanVal.slice(0, limit);
      finalPhone = "+" + truncatedClean;
      
      // Synchronously force the react-phone-input-2 internal state to update
      if (phoneInputRef.current) {
        const formatted = phoneInputRef.current.formatNumber(truncatedClean, countryData);
        phoneInputRef.current.setState({ formattedNumber: formatted });
      }
    }

    setInternalValue(finalPhone);
    lastSentValueRef.current = finalPhone;
    setFieldValue(name, finalPhone);
    if (countryFieldName && countryData?.countryCode) {
      setFieldValue(countryFieldName, String(countryData.countryCode).toUpperCase());
    }
  };

  return (
    <div className={divClassName}>
      {label && (
        <p className={`${labelClass || "mb-2 text-sm font-medium text-gray-700"}`}>
          {label}
        </p>
      )}
      <div className="relative">
        <PhoneInput
          ref={phoneInputRef}
          key={resolvedCountry}
          country={resolvedCountry}
          value={internalValue}
          onChange={handleInputChange}
          onBlur={handleBlur}
          enableSearch={true}
          disableSearchIcon={false}
          disableCountryCode={false}
          disableCountryGuess={true}
          countryCodeEditable={false}
          enableLongNumbers={true}
          autoFormat={true}
          masks={{
            ae: ".. ... ....",
            sa: ".. ... ....",
            qa: ".. ... ...",
            bh: ".... ....",
            om: ".... ....",
            kw: ".... ....",
          }}
          inputProps={{
            name: name,
            maxLength: 25,
            ...rest,
          }}
          placeholder={placeholder || "Contact Number"}
          containerClass={`react-tel-input ${containerClass || ""}`}
          inputClass={inputClass || "form-control"}
          buttonClass={buttonClass || "flag-dropdown"}
          dropdownClass="country-list"
          inputStyle={{ ...inputStyle }}
          buttonStyle={buttonStyle}
        />
      </div>
      {error && touched ? (
        <span
          className={
            errorClass ||
            "text-red-600 text-[10px] sm:text-[10px] md:text-[12px] font-[600] ms-1 mt-1 block"
          }
        >
          {error}
        </span>
      ) : null}
    </div>
  );
};

export default PhoneFormInput;
